"use client";

import { c as _c } from "react/compiler-runtime";
import ConnectWallet from "@/components/common/ConnectWallet";
import SpinnerLoader from "@/components/common/SpinnerLoader";
import HeaderUserConnect from "@/components/header/user/HeaderUserConnect";
import { formatCount } from "@/helpers/format.helpers";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import { type PublicWaveShellData, usePublicWaveShellState } from "./usePublicWaveShellState";
interface PublicWaveShellProps {
  readonly waveId: string;
}
const LOCKED_PREVIEW_ROWS = [{
  id: "preview-1",
  nameWidth: "tw-w-24",
  timeWidth: "tw-w-12",
  lines: ["tw-w-5/6", "tw-w-2/5"],
  hasAttachment: false,
  reactions: 0,
  opacityClass: "tw-opacity-90"
}, {
  id: "preview-2",
  nameWidth: "tw-w-32",
  timeWidth: "tw-w-16",
  lines: ["tw-w-11/12", "tw-w-full", "tw-w-4/5", "tw-w-3/5"],
  hasAttachment: true,
  reactions: 2,
  opacityClass: "tw-opacity-60"
}, {
  id: "preview-3",
  nameWidth: "tw-w-20",
  timeWidth: "tw-w-10",
  lines: ["tw-w-1/3"],
  hasAttachment: false,
  reactions: 1,
  opacityClass: "tw-opacity-90"
}, {
  id: "preview-4",
  nameWidth: "tw-w-28",
  timeWidth: "tw-w-14",
  lines: ["tw-w-3/4", "tw-w-5/6", "tw-w-2/5"],
  hasAttachment: false,
  reactions: 0,
  opacityClass: "tw-opacity-60"
}, {
  id: "preview-5",
  nameWidth: "tw-w-36",
  timeWidth: "tw-w-12",
  lines: ["tw-w-3/5"],
  hasAttachment: true,
  reactions: 3,
  opacityClass: "tw-opacity-90"
}, {
  id: "preview-6",
  nameWidth: "tw-w-24",
  timeWidth: "tw-w-12",
  lines: ["tw-w-full", "tw-w-4/5", "tw-w-11/12", "tw-w-1/5"],
  hasAttachment: false,
  reactions: 0,
  opacityClass: "tw-opacity-60"
}, {
  id: "preview-7",
  nameWidth: "tw-w-20",
  timeWidth: "tw-w-14",
  lines: ["tw-w-1/2"],
  hasAttachment: false,
  reactions: 0,
  opacityClass: "tw-opacity-90"
}] as const;
const REACTION_KEYS = ["first", "second", "third", "fourth"] as const;
function compactDescriptionText(text: string): string {
  const normalized = text.replaceAll(/https?:\/\/\S+/g, "").replaceAll(/\s+/g, " ").trim();
  if (normalized.length <= 240) {
    return normalized;
  }
  const shortened = normalized.slice(0, 237);
  const safeBoundary = shortened.lastIndexOf(" ");
  return `${shortened.slice(0, safeBoundary > 180 ? safeBoundary : 237)}...`;
}
function PublicWaveLockedPreview() {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="tw-absolute tw-left-1/4 tw-top-1/4 tw-size-96 tw-rounded-full tw-bg-white/5 tw-mix-blend-screen tw-blur-3xl" />;
    t1 = <div className="tw-absolute tw-bottom-1/4 tw-right-1/4 tw-size-96 tw-scale-125 tw-rounded-full tw-bg-iron-400/5 tw-mix-blend-screen tw-blur-3xl" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-inset-0 tw-overflow-hidden">{t0}{t1}<div className="tw-absolute tw-inset-0 tw-overflow-hidden tw-p-6 md:tw-p-8"><div className="tw-flex tw-h-full tw-select-none tw-flex-col tw-justify-end tw-gap-6 tw-opacity-50 tw-blur-md">{LOCKED_PREVIEW_ROWS.map(_temp3)}</div></div><div className="tw-absolute tw-inset-0 tw-bg-gradient-to-b tw-from-black/60 tw-via-black/30 tw-to-black" /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function _temp3(row) {
  return <div key={row.id} className={`tw-mx-auto tw-flex tw-w-full tw-max-w-5xl tw-items-start tw-gap-4 ${row.opacityClass}`}><div className="tw-size-11 tw-flex-shrink-0 tw-rounded-full tw-bg-iron-600 tw-shadow-sm" /><div className="tw-flex-1 tw-space-y-1.5 tw-pt-0.5"><div className="tw-mb-2 tw-flex tw-items-baseline tw-gap-3"><div className={`tw-h-3.5 tw-rounded-md tw-bg-iron-400 ${row.nameWidth}`} /><div className={`tw-h-2.5 tw-rounded-md tw-bg-iron-700 ${row.timeWidth}`} /></div><div className="tw-w-full tw-space-y-2.5">{row.lines.map(_temp)}</div>{row.hasAttachment && <div className="tw-mt-3 tw-h-48 tw-w-full tw-max-w-80 tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-800 tw-shadow-sm" />}{row.reactions > 0 && <div className="tw-flex tw-gap-2 tw-pt-2">{REACTION_KEYS.slice(0, row.reactions).map(_temp2)}</div>}</div></div>;
}
function _temp2(reactionKey) {
  return <div key={reactionKey} className="tw-h-6 tw-w-10 tw-rounded-full tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-800" />;
}
function _temp(lineWidth) {
  return <div key={lineWidth} className={`tw-h-3 tw-rounded-sm tw-bg-iron-300 ${lineWidth}`} />;
}
function PublicWaveLoadingState() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="tw-flex tw-min-h-full tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-py-8"><SpinnerLoader text="Loading wave overview..." /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function PublicWaveUnavailableState() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ConnectWallet />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function PublicWaveShellContent(t0) {
  const $ = _c(36);
  const {
    wave
  } = t0;
  const t1 = wave.descriptionPreview ?? "";
  let t2;
  if ($[0] !== t1) {
    t2 = compactDescriptionText(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const description = t2;
  const hasDescription = description.length > 0;
  let t3;
  if ($[2] !== wave.membersCount) {
    t3 = formatCount(wave.membersCount) ?? "0";
    $[2] = wave.membersCount;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const memberCount = t3;
  let t4;
  if ($[4] !== wave.postsCount) {
    t4 = formatCount(wave.postsCount) ?? "0";
    $[4] = wave.postsCount;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const postCount = t4;
  let t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <PublicWaveLockedPreview />;
    t6 = <div className="tw-pointer-events-none tw-absolute tw-left-1/2 tw-top-1/2 tw-size-96 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-scale-125 tw-rounded-full tw-bg-white/10 tw-blur-3xl" />;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="tw-absolute tw-inset-x-0 tw-top-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-white/10 tw-to-transparent tw-opacity-80" />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-opacity-5 tw-mix-blend-overlay" style={{
      backgroundImage: "url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPjxyZWN0IHdpZHRoPSI0IiBoZWlnaHQ9IjQiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIi8+PC9zdmc+')"
    }} />;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="tw-mb-8 tw-flex tw-items-center tw-gap-2.5 tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-[#151515] tw-px-3 tw-py-1.5 tw-shadow-inner"><LockClosedIcon className="tw-size-3.5 tw-text-iron-300" strokeWidth={2.5} /><div className="tw-h-3 tw-w-[1px] tw-bg-iron-700" /><span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-iron-400">Members Only</span></div>;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== wave.name) {
    t10 = <h2 className="tw-mb-4 tw-bg-gradient-to-b tw-from-white tw-to-iron-400 tw-bg-clip-text tw-text-xl tw-font-semibold tw-leading-[1.05] tw-tracking-tighter tw-text-transparent sm:tw-text-2xl md:tw-text-3xl">{wave.name}</h2>;
    $[11] = wave.name;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== description || $[14] !== hasDescription) {
    t11 = hasDescription && <p className="tw-mb-10 tw-max-w-md tw-text-pretty tw-text-sm tw-font-normal tw-text-iron-400 md:tw-text-base">{description}</p>;
    $[13] = description;
    $[14] = hasDescription;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== memberCount) {
    t12 = <span className="tw-text-lg tw-font-semibold tw-text-white md:tw-text-xl">{memberCount}</span>;
    $[16] = memberCount;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-iron-500">Joined</span>;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t12) {
    t14 = <div className="tw-flex tw-w-20 tw-flex-col tw-items-center tw-gap-1.5">{t12}{t13}</div>;
    $[19] = t12;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="tw-h-8 tw-w-px tw-bg-iron-800" />;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== postCount) {
    t16 = <span className="tw-text-lg tw-font-semibold tw-text-white md:tw-text-xl">{postCount}</span>;
    $[22] = postCount;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-iron-500">Posts</span>;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== t16) {
    t18 = <div className="tw-flex tw-w-20 tw-flex-col tw-items-center tw-gap-1.5">{t16}{t17}</div>;
    $[25] = t16;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== t14 || $[28] !== t18) {
    t19 = <div className="tw-mb-10 tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-8 tw-px-4 md:tw-gap-12">{t14}{t15}{t18}</div>;
    $[27] = t14;
    $[28] = t18;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  let t21;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="tw-relative tw-w-full tw-overflow-hidden tw-rounded-xl"><HeaderUserConnect className="tw-h-12 tw-w-full" icon={<LockClosedIcon className="tw-size-5 tw-flex-shrink-0" strokeWidth={2.5} />} label="Connect Wallet" /></div>;
    t21 = <p className="tw-mx-auto tw-mb-0 tw-mt-6 tw-max-w-sm tw-text-xs tw-font-semibold tw-tracking-wide tw-text-iron-600">This content is only available to connected wallets. Connect your wallet to continue.</p>;
    $[30] = t20;
    $[31] = t21;
  } else {
    t20 = $[30];
    t21 = $[31];
  }
  let t22;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t19) {
    t22 = <div className="tw-relative tw-flex tw-h-full tw-min-h-0 tw-min-w-0 tw-flex-col tw-overflow-hidden tw-bg-black"><div className="tw-relative tw-min-h-0 tw-flex-1 tw-overflow-hidden">{t5}{t6}<div className="tw-absolute tw-inset-0 tw-z-10 tw-overflow-y-auto tw-px-4 tw-py-8 tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300 lg:tw-px-8"><div className="tw-flex tw-min-h-full tw-items-center tw-justify-center"><section className="tw-group tw-relative tw-z-10 tw-flex tw-w-full tw-max-w-xl tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950 tw-shadow-2xl">{t7}{t8}<div className="tw-relative tw-z-10 tw-flex tw-flex-col tw-items-center tw-px-6 tw-pb-10 tw-pt-10 tw-text-center md:tw-px-10">{t9}{t10}{t11}{t19}{t20}{t21}</div></section></div></div></div></div>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t19;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  return t22;
}
export default function PublicWaveShell(t0) {
  const $ = _c(4);
  const {
    waveId
  } = t0;
  const shellState = usePublicWaveShellState(waveId);
  switch (shellState.status) {
    case "loading":
      {
        let t1;
        if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <PublicWaveLoadingState />;
          $[0] = t1;
        } else {
          t1 = $[0];
        }
        return t1;
      }
    case "unavailable":
      {
        let t1;
        if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <PublicWaveUnavailableState />;
          $[1] = t1;
        } else {
          t1 = $[1];
        }
        return t1;
      }
    case "ready":
      {
        let t1;
        if ($[2] !== shellState.wave) {
          t1 = <PublicWaveShellContent wave={shellState.wave} />;
          $[2] = shellState.wave;
          $[3] = t1;
        } else {
          t1 = $[3];
        }
        return t1;
      }
  }
}